import React from 'react';
import { BlockedEntityTab, BlockedFiltersState } from '../model/types';

interface Props {
  tab: BlockedEntityTab;
  value: BlockedFiltersState;
  reasonOptions: string[];
  reasonsLoading: boolean;
  loading: boolean;
  onChange: (value: BlockedFiltersState) => void;
  onReset: () => void;
  onRefresh: () => void;
  onExport: (format: 'csv' | 'xlsx') => void;
}

export const BlockedFilters: React.FC<Props> = ({
  tab,
  value,
  reasonOptions,
  reasonsLoading,
  loading,
  onChange,
  onReset,
  onRefresh,
  onExport,
}) => {
  const patch = (next: Partial<BlockedFiltersState>) =>
    onChange({ ...value, ...next });

  return (
    <section className="blocked-filters" aria-label="Blocked entities filters">
      <div className="blocked-filters__main">
        <label className="blocked-filters__search">
          <span className="blocked-filters__sr-only">Search</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
          </svg>
          <input
            value={value.search}
            onChange={(event) => patch({ search: event.target.value })}
            placeholder={`Search ${tab} by ID, name or username`}
          />
          {value.search ? (
            <button
              type="button"
              onClick={() => patch({ search: '' })}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </label>

        <label className="blocked-filters__control">
          <span>Blocking reason</span>
          <select
            value={value.reason}
            onChange={(event) => patch({ reason: event.target.value })}
            disabled={reasonsLoading}
          >
            <option value="">
              {reasonsLoading ? 'Loading reasons…' : 'All reasons'}
            </option>
            {reasonOptions.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
        </label>

        <label className="blocked-filters__control blocked-filters__date">
          <span>Blocked from</span>
          <input
            type="date"
            value={value.dateFrom}
            onChange={(event) => patch({ dateFrom: event.target.value })}
          />
        </label>

        <label className="blocked-filters__control blocked-filters__date">
          <span>Blocked to</span>
          <input
            type="date"
            value={value.dateTo}
            min={value.dateFrom || undefined}
            onChange={(event) => patch({ dateTo: event.target.value })}
          />
        </label>

        <label className="blocked-filters__control">
          <span>Sort by</span>
          <select
            value={value.sortBy}
            onChange={(event) =>
              patch({
                sortBy: event.target.value as BlockedFiltersState['sortBy'],
              })
            }
          >
            <option value="blocked_at">Date blocked</option>
            <option value="name">Name / username</option>
            <option value="created_at">Creation date</option>
          </select>
        </label>

        <button
          type="button"
          className="blocked-filters__direction"
          onClick={() =>
            patch({ sortOrder: value.sortOrder === 'asc' ? 'desc' : 'asc' })
          }
          title={value.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 7h11M8 12h8M8 17h5M4 5v14m0 0-2.5-2.5M4 19l2.5-2.5" />
          </svg>
          {value.sortBy === 'name'
            ? value.sortOrder === 'asc'
              ? 'A–Z'
              : 'Z–A'
            : value.sortOrder === 'asc'
              ? 'Oldest first'
              : 'Newest first'}
        </button>
      </div>

      <div className="blocked-filters__actions">
        <div
          className="blocked-filters__export"
          aria-label={`Export blocked ${tab}`}
        >
          <button
            type="button"
            onClick={() => onExport('csv')}
            disabled={loading}
          >
            CSV
          </button>
          <button
            type="button"
            onClick={() => onExport('xlsx')}
            disabled={loading}
          >
            XLSX
          </button>
        </div>
        <button
          type="button"
          className="blocked-filters__secondary"
          onClick={onReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="blocked-filters__refresh"
          onClick={onRefresh}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 6v5h-5M4 18v-5h5m10.2-3A8 8 0 0 0 5.4 6.4L4 8m16 8-1.4 1.6A8 8 0 0 1 4.8 14" />
          </svg>
          Refresh
        </button>
      </div>
    </section>
  );
};
